import './styles.css';
import { AmbientLight, Color, DirectionalLight, Group, Mesh, MeshStandardMaterial, PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { download } from './lib/exporters';
import { exportModel } from './lib/exportJob';
import { generateModel } from './lib/model';
import type { GeneratedModel, ModelSettings, ProcessingOptions, WorkerRequest, WorkerResponse } from './lib/types';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('找不到应用容器。');

app.innerHTML = `
  <main class="shell">
    <aside class="sidebar" aria-label="掐丝参数">
      <div class="brand"><span class="brand-mark">丝</span><div><strong>掐丝工坊</strong><small>浏览器内完成，不上传图片</small></div></div>
      <section class="mode-card">
        <span class="eyebrow">使用模式</span>
        <div class="segmented" role="group" aria-label="使用模式"><button class="segment active" data-mode="simple">极简模式</button><button class="segment" data-mode="pro">专业模式</button></div>
        <p>极简模式保留上传、尺寸、高度和底板；专业模式可修线与统一线宽。</p>
      </section>
      <section class="control-section upload-control">
        <div class="section-heading"><span>上传图像</span><span id="fileName">等待上传</span></div>
        <label class="upload-zone" for="fileInput"><input id="fileInput" type="file" accept="image/png,image/jpeg,image/webp"/><span>拖入图片，或点击选择</span><small>PNG / JPG / WebP，建议线稿边缘清晰</small></label>
      </section>
      <section class="control-section simple-control">
        <label>最终宽度 <output id="printWidthOut">100 mm</output><input id="printWidth" type="range" min="40" max="240" value="100" step="1" data-render/></label>
        <div class="two-col"><label>线条高度<input id="lineHeight" type="number" min="0.4" max="8" value="2" step="0.1" data-render/></label><label>当前高度<output id="printHeight">–</output></label></div>
      </section>
      <section class="control-section simple-control">
        <div class="section-heading"><span>底板设置</span><span id="plateSummary">矩形 · 1 mm</span></div>
        <label>底板形状<select id="plateType" data-render><option value="none">无底板</option><option value="contour">贴合轮廓</option><option value="circle">圆形</option><option value="rectangle" selected>矩形</option></select></label>
        <div class="two-col"><label>底板厚度<input id="plateThickness" type="number" min="0.4" max="6" value="1" step="0.1" data-render/></label><label>最小尺寸<input id="plateSize" type="number" min="0" max="300" value="0" step="1" data-render/></label></div>
        <label class="contour-margin">轮廓留边 <output id="contourMarginOut">2 mm</output><input id="contourMargin" type="range" min="0" max="8" value="2" step="0.25" data-render/></label>
      </section>
      <section class="control-section pro-control">
        <div class="section-heading"><span>图片处理</span><span>先修线稿</span></div>
        <label>图片类型<select id="sourceMode" data-process><option value="line">黑白线稿</option><option value="photo">彩色照片 / 边缘提取</option></select></label>
        <label>对比度阈值 <output id="thresholdOut">150</output><input id="threshold" type="range" min="15" max="245" value="150" step="1" data-process/></label>
        <label class="switch-line"><input id="invert" type="checkbox" data-process/><span>黑白反转</span></label>
        <div class="two-col"><label>去噪<input id="denoise" type="number" min="0" max="3" value="0" step="1" data-process/></label><label>闭合断口<input id="closeGaps" type="number" min="0" max="3" value="1" step="1" data-process/></label></div>
        <label>最小区域过滤 <output id="minIslandOut">12 px</output><input id="minIsland" type="range" min="0" max="160" value="12" step="2" data-process/></label>
      </section>
      <section class="control-section pro-control">
        <div class="section-heading"><span>统一线宽</span><span id="lineWidthSummary">保留原稿</span></div>
        <label class="switch-line"><input id="uniformWidth" type="checkbox" data-process/><span>按最终毫米尺寸统一线宽</span></label>
        <label>目标线宽 <output id="lineWidthOut">0.8 mm</output><input id="targetLineWidth" type="range" min="0.4" max="2.4" value="0.8" step="0.1" data-process/></label>
        <label>轮廓光滑度 <output id="smoothnessOut">3</output><input id="smoothness" type="range" min="1" max="10" value="3" step="1" data-render/></label>
      </section>
      <div class="sidebar-action"><button id="generateButton" class="primary-action">生成 SVG / 3D</button><button id="cancelButton" class="quiet-action" hidden>取消处理</button></div>
    </aside>
    <section class="workspace">
      <header class="workspace-header"><div><span id="stateDot" class="state-dot"></span><strong id="statusText">等待上传</strong><small id="statusDetail">上传线稿，处理后再生成可打印模型。</small></div><div class="progress-wrap"><progress id="progress" max="100" value="0"></progress><span id="progressLabel">0%</span></div></header>
      <div class="flow-pill">流程：上传图片 → 图片处理 → 统一线宽 → 生成 SVG → 3D 预览 / 导出</div>
      <div class="panes">
        <article class="panel image-panel"><div class="panel-header"><div><h1>图片处理结果</h1><p>黑白线稿是实际建模的输入</p></div><span id="lineStatus" class="panel-chip">等待上传</span></div><div id="lineCanvasWrap" class="line-canvas-wrap"><canvas id="lineCanvas" aria-label="可编辑线稿画布"></canvas><div id="emptyLine" class="empty-panel"><strong>进入图片处理后显示黑白线稿</strong><span>上传清晰的线稿、照片或透明 PNG</span></div></div><div class="canvas-toolbar pro-control"><div class="tool-buttons"><button class="tool active" data-tool="brush">补线画笔</button><button class="tool" data-tool="eraser">橡皮</button><label>尺寸 <input id="brushSize" type="range" min="2" max="40" value="10"/><output id="brushSizeOut">10 px</output></label></div><div><button id="undoButton" class="tool" disabled>撤销</button><button id="redoButton" class="tool" disabled>重做</button></div></div></article>
        <article class="panel model-panel"><div class="panel-header"><div><h1>3D 模型预览</h1><p>拖动旋转，滚轮缩放</p></div><button id="resetView" class="panel-chip">复位视角</button></div><div id="viewport" class="viewport"><div id="emptyModel" class="empty-panel inverse"><strong>生成后显示真实可导出模型</strong><span>底板和线条可以分别设色预览</span></div></div><div class="model-options"><label>线条颜色<input id="wireColor" type="color" value="#b64c35"/></label><label>底板颜色<input id="baseColor" type="color" value="#ead8b4"/></label><div><span>模式</span><strong id="modelMode">未生成</strong></div><div><span>模型尺寸</span><strong id="modelSize">–</strong></div><div><span>三角面</span><strong id="triangleCount">–</strong></div></div><div class="downloads"><button id="svgButton" disabled>下载真 SVG</button><button id="stlButton" disabled>导出 STL</button><button id="threeMfButton" disabled>导出双色 3MF</button></div></article>
      </div>
      <div id="exportPanel" class="export-panel" hidden><span id="exportStatus" role="status" aria-live="polite"></span><button id="cancelExport" class="quiet-action" hidden>取消导出</button><a id="saveFile" hidden></a></div>
      <p id="modelNotice" class="model-notice">导出前请确认线条连续；无底板时，模型可能含有分离线条。</p>
    </section>
  </main>`;
app.classList.add('simple-mode');

const byId = <T extends HTMLElement>(id: string) => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`找不到 #${id}`);
  return element as T;
};
const value = (id: string) => Number((byId<HTMLInputElement>(id)).value);
const input = (id: string) => byId<HTMLInputElement>(id);

let sourceImage: ImageData | null = null;
let processedImage: ImageData | null = null;
let model: GeneratedModel | null = null;
let modelStale = true;
let activeTool: 'brush' | 'eraser' = 'brush';
let painting = false;
let processRequest = 0;
let processTimer = 0;
let exportController: AbortController | null = null;
let releaseDownload: (() => void) | null = null;
const undoStack: ImageData[] = [];
const redoStack: ImageData[] = [];

const worker = new Worker(new URL('./imageWorker.ts', import.meta.url), { type: 'module' });
const lineCanvas = byId<HTMLCanvasElement>('lineCanvas');
const lineContextCandidate = lineCanvas.getContext('2d', { willReadFrequently: true });
if (!lineContextCandidate) throw new Error('浏览器不支持画布。');
const lineContext: CanvasRenderingContext2D = lineContextCandidate;

const scene = new Scene();
scene.background = new Color('#173c35');
const camera = new PerspectiveCamera(35, 1, 0.1, 1200);
const renderer = new WebGLRenderer({ antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = 'srgb';
const viewport = byId<HTMLDivElement>('viewport');
viewport.append(renderer.domElement);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.target.set(0, 0, 0);
camera.position.set(100, -130, 115);
scene.add(new AmbientLight('#fff6df', 2.2));
const light = new DirectionalLight('#fff4d8', 3.5);
light.position.set(100, -70, 130);
scene.add(light);
const modelGroup = new Group();
scene.add(modelGroup);
let wireMaterial = new MeshStandardMaterial({ color: '#b64c35', metalness: 0.1, roughness: 0.52 });
let baseMaterial = new MeshStandardMaterial({ color: '#ead8b4', metalness: 0, roughness: 0.72 });

function settings(): ModelSettings {
  return { printWidth: value('printWidth'), lineHeight: value('lineHeight'), plateType: byId<HTMLSelectElement>('plateType').value as ModelSettings['plateType'], plateThickness: value('plateThickness'), plateSize: value('plateSize'), contourMargin: value('contourMargin'), smoothness: value('smoothness') };
}

function processing(): ProcessingOptions {
  return { sourceMode: byId<HTMLSelectElement>('sourceMode').value as ProcessingOptions['sourceMode'], threshold: value('threshold'), invert: input('invert').checked, denoise: value('denoise'), closeGaps: value('closeGaps'), minIsland: value('minIsland'), uniformWidth: input('uniformWidth').checked, targetLineWidth: value('targetLineWidth'), printWidth: value('printWidth') };
}

function setStatus(title: string, detail: string, progress = 0, working = false) {
  byId('statusText').textContent = title;
  byId('statusDetail').textContent = detail;
  byId<HTMLProgressElement>('progress').value = progress;
  byId('progressLabel').textContent = `${Math.round(progress)}%`;
  byId('stateDot').classList.toggle('working', working);
  byId<HTMLButtonElement>('cancelButton').hidden = !working;
}

function updateControlLabels() {
  byId('printWidthOut').textContent = `${value('printWidth')} mm`;
  byId('thresholdOut').textContent = input('threshold').value;
  byId('minIslandOut').textContent = `${value('minIsland')} px`;
  byId('lineWidthOut').textContent = `${value('targetLineWidth').toFixed(1)} mm`;
  byId('contourMarginOut').textContent = `${value('contourMargin')} mm`;
  byId('smoothnessOut').textContent = input('smoothness').value;
  byId('brushSizeOut').textContent = `${value('brushSize')} px`;
  byId('lineWidthSummary').textContent = input('uniformWidth').checked ? `${value('targetLineWidth').toFixed(1)} mm` : '保留原稿';
  const plate = byId<HTMLSelectElement>('plateType').selectedOptions[0].textContent ?? '无底板';
  byId('plateSummary').textContent = input('plateType').value === 'none' ? plate : `${plate} · ${value('plateThickness')} mm`;
}

function downloadButtons() {
  const disabled = !model || modelStale || exportController !== null;
  for (const id of ['svgButton', 'stlButton', 'threeMfButton']) byId<HTMLButtonElement>(id).disabled = disabled;
}

function markStale() {
  clearExport();
  if (!model) return;
  modelStale = true;
  downloadButtons();
  byId('modelNotice').textContent = '参数已改变，请重新生成后再导出，避免下载旧模型。';
}

function cloneImage(image: ImageData) {
  return new ImageData(new Uint8ClampedArray(image.data), image.width, image.height);
}

function paintImage(image: ImageData) {
  lineCanvas.width = image.width;
  lineCanvas.height = image.height;
  lineContext.putImageData(image, 0, 0);
  byId('emptyLine').hidden = true;
  byId('lineStatus').textContent = `线稿 ${image.width} × ${image.height}`;
}

function updateHistoryButtons() {
  byId<HTMLButtonElement>('undoButton').disabled = undoStack.length === 0;
  byId<HTMLButtonElement>('redoButton').disabled = redoStack.length === 0;
}

function rememberEdit() {
  if (!processedImage) return;
  undoStack.push(cloneImage(processedImage));
  if (undoStack.length > 20) undoStack.shift();
  redoStack.splice(0, redoStack.length);
  updateHistoryButtons();
}

function resizeViewport() {
  const { width, height } = viewport.getBoundingClientRect();
  if (!width || !height) return;
  renderer.setSize(width, height, false);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
}
new ResizeObserver(resizeViewport).observe(viewport);

function animate() {
  controls.update();
  renderer.render(scene, camera);
  requestAnimationFrame(animate);
}
animate();

function clearPreview() {
  while (modelGroup.children.length) {
    const child = modelGroup.children.pop();
    if (child instanceof Mesh) child.geometry.dispose();
  }
}

function setPreviewColors() {
  wireMaterial.color.set(input('wireColor').value);
  baseMaterial.color.set(input('baseColor').value);
}

function resetView() {
  if (!model) {
    camera.position.set(100, -130, 115);
    controls.target.set(0, 0, 0);
  } else {
    const span = Math.max(model.width, model.height, 30);
    camera.position.set(span * 1.05, -span * 1.25, span * 1.15);
    controls.target.set(0, 0, settings().plateType === 'none' ? settings().lineHeight / 2 : settings().plateThickness / 2);
  }
  controls.update();
}

function renderModel(next: GeneratedModel) {
  clearPreview();
  if (next.baseGeometry) modelGroup.add(new Mesh(next.baseGeometry, baseMaterial));
  modelGroup.add(new Mesh(next.wireGeometry, wireMaterial));
  byId('emptyModel').hidden = true;
  byId('modelMode').textContent = next.baseGeometry ? '双色分件' : '单色线条';
  byId('modelSize').textContent = `${next.width.toFixed(1)} × ${next.height.toFixed(1)} mm`;
  byId('triangleCount').textContent = next.triangleCount.toLocaleString('zh-CN');
  resetView();
}

function scheduleProcessing() {
  updateControlLabels();
  markStale();
  if (!sourceImage) return;
  window.clearTimeout(processTimer);
  processTimer = window.setTimeout(runProcessing, 180);
}

function runProcessing() {
  if (!sourceImage) return;
  const requestId = ++processRequest;
  setStatus('正在处理图片', '清理线稿和连接断口。', 4, true);
  worker.postMessage({ type: 'process', requestId, width: sourceImage.width, height: sourceImage.height, pixels: sourceImage.data.buffer.slice(0), options: processing() } satisfies WorkerRequest);
}

worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  const message = event.data;
  if (message.requestId !== processRequest) return;
  if (message.type === 'progress') {
    setStatus('正在处理图片', message.label, message.value, true);
    return;
  }
  if (message.type === 'error') {
    setStatus('图片处理失败', message.message, 0, false);
    return;
  }
  processedImage = new ImageData(new Uint8ClampedArray(message.pixels), message.width, message.height);
  undoStack.splice(0, undoStack.length);
  redoStack.splice(0, redoStack.length);
  updateHistoryButtons();
  paintImage(processedImage);
  const percent = ((message.wirePixels / (message.width * message.height)) * 100).toFixed(1);
  setStatus('线稿处理完成', `线条覆盖 ${percent}%，可继续修线或生成模型。`, 100, false);
  markStale();
};

async function loadFile(file: File) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    setStatus('不支持该格式', '请选择 PNG、JPG 或 WebP 图片。', 0, false);
    return;
  }
  const bitmap = await createImageBitmap(file);
  clearExport();
  const maxSide = 900;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('浏览器不支持画布。');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  sourceImage = context.getImageData(0, 0, width, height);
  processedImage = null;
  byId('fileName').textContent = file.name;
  byId('lineStatus').textContent = '正在处理';
  modelStale = true;
  downloadButtons();
  runProcessing();
}

function placeBrush(event: PointerEvent) {
  if (!painting || !processedImage) return;
  const rect = lineCanvas.getBoundingClientRect();
  const x = (event.clientX - rect.left) * lineCanvas.width / rect.width;
  const y = (event.clientY - rect.top) * lineCanvas.height / rect.height;
  lineContext.fillStyle = activeTool === 'brush' ? '#181818' : '#ffffff';
  lineContext.beginPath();
  lineContext.arc(x, y, value('brushSize') / 2, 0, Math.PI * 2);
  lineContext.fill();
  processedImage = lineContext.getImageData(0, 0, lineCanvas.width, lineCanvas.height);
  markStale();
  byId('lineStatus').textContent = '已手动修线';
}

lineCanvas.addEventListener('pointerdown', (event) => {
  if (!processedImage) return;
  painting = true;
  rememberEdit();
  lineCanvas.setPointerCapture(event.pointerId);
  placeBrush(event);
});
lineCanvas.addEventListener('pointermove', placeBrush);
lineCanvas.addEventListener('pointerup', (event) => { painting = false; if (lineCanvas.hasPointerCapture(event.pointerId)) lineCanvas.releasePointerCapture(event.pointerId); });
lineCanvas.addEventListener('pointercancel', () => { painting = false; });

byId<HTMLInputElement>('fileInput').addEventListener('change', (event) => {
  const file = ((event.currentTarget as HTMLInputElement).files ?? [])[0];
  if (file) void loadFile(file).catch((error: unknown) => setStatus('上传失败', error instanceof Error ? error.message : '无法读取图片。', 0, false));
});
const uploadZone = byId<HTMLLabelElement>('fileInput').closest('label');
uploadZone?.addEventListener('dragover', (event) => { event.preventDefault(); uploadZone.classList.add('dragging'); });
uploadZone?.addEventListener('dragleave', () => uploadZone.classList.remove('dragging'));
uploadZone?.addEventListener('drop', (event) => { event.preventDefault(); uploadZone.classList.remove('dragging'); const file = event.dataTransfer?.files[0]; if (file) void loadFile(file).catch(() => setStatus('上传失败', '无法读取该图片。', 0, false)); });

document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-process]').forEach((element) => element.addEventListener('input', scheduleProcessing));
document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-render]').forEach((element) => element.addEventListener('input', () => { updateControlLabels(); markStale(); }));
input('brushSize').addEventListener('input', updateControlLabels);
input('wireColor').addEventListener('input', setPreviewColors);
input('baseColor').addEventListener('input', setPreviewColors);

document.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => button.addEventListener('click', () => {
  document.querySelectorAll('[data-mode]').forEach((other) => other.classList.toggle('active', other === button));
  app.classList.toggle('simple-mode', button.dataset.mode === 'simple');
}));
document.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach((button) => button.addEventListener('click', () => {
  activeTool = button.dataset.tool as typeof activeTool;
  document.querySelectorAll('[data-tool]').forEach((other) => other.classList.toggle('active', other === button));
}));
byId<HTMLButtonElement>('undoButton').addEventListener('click', () => {
  if (!processedImage || !undoStack.length) return;
  redoStack.push(cloneImage(processedImage));
  processedImage = undoStack.pop() ?? null;
  if (processedImage) paintImage(processedImage);
  markStale();
  updateHistoryButtons();
});
byId<HTMLButtonElement>('redoButton').addEventListener('click', () => {
  if (!processedImage || !redoStack.length) return;
  undoStack.push(cloneImage(processedImage));
  processedImage = redoStack.pop() ?? null;
  if (processedImage) paintImage(processedImage);
  markStale();
  updateHistoryButtons();
});
byId<HTMLButtonElement>('cancelButton').addEventListener('click', () => { if (exportController) { cancelExport(); return; } processRequest += 1; setStatus('已取消处理', '保留当前线稿。', 0, false); });
byId<HTMLButtonElement>('cancelExport').addEventListener('click', cancelExport);
byId<HTMLButtonElement>('resetView').addEventListener('click', resetView);

byId<HTMLButtonElement>('generateButton').addEventListener('click', async () => {
  if (!processedImage) {
    setStatus('请先上传图片', '上传后会自动生成可编辑的黑白线稿。', 0, false);
    return;
  }
  const button = byId<HTMLButtonElement>('generateButton');
  markStale();
  button.disabled = true;
  setStatus('正在生成模型', '矢量化线稿并构建可靠实体。', 20, true);
  try {
    const next = await generateModel(processedImage, settings());
    clearPreview();
    model = next;
    modelStale = false;
    renderModel(next);
    downloadButtons();
    const description = next.baseGeometry ? '已生成整体 STL 和底板、线条分件 3MF。' : '未使用底板；导出前请确认所有线条都能单独打印。';
    byId('modelNotice').textContent = description;
    setStatus('模型生成完成', `${next.width.toFixed(1)} × ${next.height.toFixed(1)} mm，${next.triangleCount.toLocaleString('zh-CN')} 个三角面。`, 100, false);
  } catch (error) {
    setStatus('模型生成失败', error instanceof Error ? error.message : '请尝试降低图片复杂度或补线。', 0, false);
  } finally {
    button.disabled = false;
  }
});

function currentModel() {
  if (!model || modelStale) {
    setStatus('请重新生成模型', '参数或线稿已改变，下载被阻止以避免导出旧结果。', 0, false);
    return null;
  }
  return model;
}

function clearExport() {
  if (exportController) setStatus('导出已取消', '可根据当前参数重新生成模型后导出。');
  exportController?.abort();
  exportController = null;
  releaseDownload?.();
  releaseDownload = null;
  byId('exportPanel').hidden = true;
  byId('cancelExport').hidden = true;
  downloadButtons();
}

function cancelExport() {
  clearExport();
  byId('exportPanel').hidden = false;
  byId('exportStatus').textContent = '已取消导出，可重新选择格式。';
  setStatus('已取消导出', '当前模型仍可预览和重新导出。');
}

async function startExport(format: 'svg' | 'stl' | '3mf') {
  if (exportController) return;
  const current = currentModel();
  if (!current) return;
  clearExport();
  const controller = new AbortController();
  exportController = controller;
  downloadButtons();
  byId('exportPanel').hidden = false;
  byId('cancelExport').hidden = false;
  const label = format.toUpperCase();
  const progress = (percent: number) => {
    const detail = `${label} 正在打包 ${Math.floor(percent)}%，完成后可保存。`;
    byId('exportStatus').textContent = detail;
    setStatus('正在准备下载', detail, percent, true);
  };
  progress(0);
  try {
    const blob = format === 'svg' ? new Blob([current.svg], { type: 'image/svg+xml' })
      : await exportModel(current, format, controller.signal, progress);
    if (controller.signal.aborted || current !== model || modelStale) return;
    const filename = format === 'svg' ? '掐丝工坊.svg' : format === 'stl' ? '掐丝工坊-整体.stl' : '掐丝工坊-双色分件.3mf';
    releaseDownload = download(blob, filename, byId<HTMLAnchorElement>('saveFile'));
    byId('exportStatus').textContent = '文件已准备好。如果没有自动下载，请点击保存链接。';
    setStatus(`${label} 已准备好`, '已交给浏览器下载；也可点击下方保存链接重试。', 100);
  } catch (error) {
    if (controller.signal.aborted) return;
    const message = error instanceof Error ? error.message : '请重试。';
    byId('exportStatus').textContent = `导出失败：${message}`;
    setStatus('导出失败', message);
  } finally {
    if (exportController === controller) {
      exportController = null;
      byId('cancelExport').hidden = true;
      downloadButtons();
    }
  }
}

byId<HTMLButtonElement>('svgButton').addEventListener('click', () => { void startExport('svg'); });
byId<HTMLButtonElement>('stlButton').addEventListener('click', () => { void startExport('stl'); });
byId<HTMLButtonElement>('threeMfButton').addEventListener('click', () => { void startExport('3mf'); });
window.addEventListener('pagehide', clearExport);

updateControlLabels();
downloadButtons();
resizeViewport();
